import createAuthClient from "@adsk/identity-web-sdk"
import type { AuthClient } from "@adsk/identity-web-sdk"

let _authClientPromise: Promise<AuthClient> | null = null
let _clientId: string | null = null
let _env: string | null = null

const _ADSK_CLIENT_CONFIG_KEY = "autodesk_client_config"

/**
 * Return the cached auth client Promise, creating it if the config has changed.
 *
 * Stores a Promise so that repeated calls with the same config share a single
 * SDK initialisation without triggering duplicate createAuthClient calls.
 *
 * Parameters:
 * clientId (string): The APS SPA client ID registered in the Autodesk Developer Portal.
 * env (string): Target environment — "prod" for production, "stg" for staging.
 *
 * Returns:
 * Promise<AuthClient>: Resolves to the Autodesk Identity auth client instance.
 */
function getOrCreateAuthClientPromise(clientId: string, env: string): Promise<AuthClient> {
    if (!_authClientPromise || _clientId !== clientId || _env !== env) {
        _authClientPromise = createAuthClient({
            client_id: clientId,
            redirect_uri: `${window.location.origin}/oauth/callback`,
            redirect_uri_popup: `${window.location.origin}/oauth/callback`,
            env,
            // The SDK appends 'openid' automatically, so the effective scope is
            // 'data:read profapi:core-std-profile:read openid'.
            // 'profapi:core-std-profile:read' is required by Autodesk for the
            // /userinfo endpoint to return the user's email and profile fields.
            scope: "data:read openid profapi:core-std-profile:read",
            useRefreshTokens: true,
            useReactContext: true,
            cacheLocation: "localstorage",
            enableLocalNetworkAccess: window.location.hostname === "localhost",
        })
        _clientId = clientId
        _env = env
    }
    return _authClientPromise
}

/**
 * Persist the Autodesk client configuration to localStorage so that
 * autodeskLogout() can revoke tokens even after a full page refresh.
 *
 * Call this immediately after a successful Autodesk login.
 *
 * Parameters:
 * clientId (string): The APS SPA client ID.
 * env (string): Target environment — "prod" or "stg".
 */
export function storeAutodeskClientConfig(clientId: string, env: string): void {
    try {
        localStorage.setItem(_ADSK_CLIENT_CONFIG_KEY, JSON.stringify({ clientId, env }))
    } catch {
        // Non-fatal: if localStorage is unavailable, logout will fall back to module state.
    }
}

/**
 * Revoke Autodesk tokens and clear the SDK's local state.
 *
 * Reads the stored client configuration from module state (available within
 * the same page session) or localStorage (available after a page refresh).
 * Calls client.logout({ localOnly: true }) which revokes the access token and
 * refresh token via Autodesk's /authentication/v2/revoke endpoint, then clears
 * all SDK localStorage keys.  The function is best-effort: any error is silently
 * swallowed so that the caller's logout flow (e.g. POST /api/v3/users/logout/)
 * always continues.  The stored config entry is removed from localStorage on
 * completion regardless of whether the SDK call succeeded.
 *
 * Returns:
 * Promise<void>
 */
export async function autodeskLogout(): Promise<void> {
    let clientId = _clientId
    let env = _env

    if (!clientId || !env) {
        try {
            const stored = localStorage.getItem(_ADSK_CLIENT_CONFIG_KEY)
            if (stored) {
                const parsed = JSON.parse(stored) as { clientId?: string; env?: string }
                clientId = parsed.clientId ?? null
                env = parsed.env ?? null
            }
        } catch {
            // Non-fatal: proceed without Autodesk SDK logout.
        }
    }

    if (!clientId || !env) {
        return
    }

    try {
        const client = await getOrCreateAuthClientPromise(clientId, env)
        await client.logout({ localOnly: true })
    } catch {
        // Non-fatal: Rhumbix logout continues regardless of Autodesk SDK errors.
    } finally {
        try {
            localStorage.removeItem(_ADSK_CLIENT_CONFIG_KEY)
        } catch {
            // Ignore localStorage errors.
        }
    }
}

/**
 * Open an Autodesk Identity popup via the SDK's built-in loginWithPopup, authenticate
 * the user, and return the resulting access and ID tokens for exchange with the Rhumbix
 * backend.
 *
 * Delegates entirely to client.loginWithPopup(), which handles PKCE, state generation,
 * popup opening, URL polling, and code exchange internally.
 *
 * Parameters:
 * clientId (string): The APS SPA client ID from the backend availability response.
 * env (string): Target environment — "prod" for production, "stg" for staging.
 * email (string): Optional login hint to pre-fill the Autodesk sign-in form.
 *
 * Returns:
 * Promise<{accessToken: string, idToken: string|null}>: Resolves with the Autodesk
 *   access token and OIDC ID token on successful authentication.  The ID token is
 *   best-effort and may be null if the SDK cache is unavailable.
 *
 * Throws:
 * Error: If the browser blocks the popup before the SDK is even invoked.
 * PopupCancelledError: If the popup is closed before authentication completes (including
 *   if COOP on Autodesk's auth pages severs the window reference prematurely).
 * PopupTimeoutError: If the user does not complete authentication within 15 minutes.
 * GenericError (open_popup_failed): If the browser blocks the popup at the SDK level.
 */
export async function autodeskLoginWithPopup(clientId: string, env: string, email: string): Promise<{ accessToken: string; idToken: string | null }> {
    const testPopup = window.open("about:blank", "_blank", "width=1,height=1")
    if (!testPopup) {
        throw new Error("The authentication popup was blocked")
    }
    testPopup.close()

    const client = await getOrCreateAuthClientPromise(clientId, env)

    const accessToken = await client.loginWithPopup(email ? { login_hint: email } : {})

    // Retrieve the OIDC ID token from the SDK cache.  It was stored during the PKCE
    // token exchange above and is guaranteed by the OIDC spec to include the 'sub'
    // claim (Autodesk user ID) plus profile fields when the email/profile scopes are
    // present.  Sending it to the backend avoids the need to call /userinfo for user
    // identification.  This call is best-effort — if the cache is unavailable the
    // backend falls back to /userinfo.
    let idToken: string | null = null
    try {
        idToken = await client.getIDTokenSilently()
    } catch {
        // Non-fatal: the backend will fall back to /userinfo to identify the user.
    }

    return { accessToken, idToken }
}
